import {
  DEFAULT_SHAPE_STYLE,
  createDraw,
  createShape,
  drawWorldPoints,
  eraseStrokeAt,
  hitTestNode,
  nodeAABB,
  nodeBox,
  recognizeStroke,
  rectCenter,
  rectIntersects,
  simplify,
  vDist,
  vRotate,
  type DrawPoint,
  type NodeId,
  type Vec,
} from '@kanvas/board-core';
import { invalidate, scratch } from '../../state/scratch';
import { useEditor } from '../../state/store';
import { slop } from '../picking';
import type { Gesture, GestureContext } from '../types';

/** Minimum spacing between stored samples, in screen pixels. */
const SAMPLE_SPACING_PX = 1.8;
/** Simplification tolerance applied on commit, in screen pixels. */
const SIMPLIFY_PX = 0.7;
/**
 * How often the in-progress stroke is broadcast to peers.
 *
 * Peers ease between packets, so this sets how far behind the pen they can be
 * rather than how smooth their copy looks.
 */
const DRAFT_BROADCAST_MS = 40;

/**
 * Freehand drawing.
 *
 * Samples are thinned on the way in (by distance, also capped to a fraction of
 * the brush width so zooming out cannot starve a letter of points) and
 * simplified on commit, so a long stroke stays a few hundred points rather
 * than a few thousand. The stroke is only written to the document once, on
 * pointerup: peers watch it live through the presence channel instead, which
 * costs no CRDT history.
 */
export function createDrawGesture(ctx: GestureContext): Gesture {
  const doc = ctx.doc;
  const style = useEditor.getState().drawStyle;
  const points: DrawPoint[] = [[ctx.pointer.board.x, ctx.pointer.board.y, ctx.pointer.pressure]];

  scratch.drawPoints = points;
  scratch.drawColor = style.color;
  scratch.drawSize = style.size;
  scratch.drawBrush = style.brush;
  scratch.gestureActive = true;
  scratch.brushCursorPoint = ctx.pointer.board;

  let lastBroadcast = 0;

  const broadcast = (force = false) => {
    const now = performance.now();
    if (!force && now - lastBroadcast < DRAFT_BROADCAST_MS) return;
    lastBroadcast = now;
    doc.setPresence({
      // Presence has no notion of a delta, so every packet carries the whole
      // stroke so far. Samples therefore go out rounded: a tenth of a board
      // unit is far below anything a stroke can show, and it stops a long
      // stroke's packets from growing faster than the drawing itself.
      draft: {
        points: points.map(
          (p) => [round(p[0], 10), round(p[1], 10), round(p[2], 100)] as DrawPoint,
        ),
        color: style.color,
        size: style.size,
        brush: style.brush,
      },
    });
  };

  return {
    cursor: 'none',
    coalesce: true,
    move(next) {
      const vp = useEditor.getState().viewport;
      // Cap spacing by stroke width so zooming out cannot undersample a letter.
      const spacing = Math.min(SAMPLE_SPACING_PX / vp.zoom, Math.max(style.size * 0.35, 0.75));
      const last = points[points.length - 1]!;
      if (vDist({ x: last[0], y: last[1] }, next.pointer.board) < spacing) return;

      points.push([next.pointer.board.x, next.pointer.board.y, next.pointer.pressure]);
      scratch.brushCursorPoint = next.pointer.board;
      broadcast();
      invalidate();
    },

    up(next) {
      const vp = useEditor.getState().viewport;
      scratch.drawPoints = null;
      scratch.gestureActive = false;
      doc.setPresence({ draft: null });

      if (points.length === 0) {
        invalidate();
        return;
      }

      const positions = points.map((p) => ({ x: p[0], y: p[1] }));

      // Magic wand: a stroke that closes on itself or runs dead straight snaps
      // to the perfect primitive it was probably meant to be. Recognition only
      // ever swaps the *result*; the gesture itself never sees it, so a false
      // positive is exactly one Ctrl+Z away from plain ink.
      if (useEditor.getState().shapeAssistEnabled) {
        const recognized = recognizeStroke(positions);
        if (recognized) {
          const factoryCtx = { userId: next.identity.userId, index: doc.topIndex() };

          if (recognized.kind === 'line') {
            const [a, b] = recognized.points!;
            const node = createDraw(
              [
                [a.x, a.y, points[0]![2]],
                [b.x, b.y, points[points.length - 1]![2]],
              ],
              factoryCtx,
              { color: style.color, size: style.size, brush: style.brush },
            );
            doc.add(node);
          } else {
            const node = createShape(
              recognized.kind,
              recognized.box.x,
              recognized.box.y,
              factoryCtx,
              {
                w: Math.max(recognized.box.w, 1),
                h: Math.max(recognized.box.h, 1),
                style: {
                  ...DEFAULT_SHAPE_STYLE,
                  fill: 'transparent',
                  fillStyle: 'none',
                  stroke: style.color,
                  strokeWidth: style.size,
                },
              },
            );
            doc.add(node);
          }

          doc.breakUndoBatch();
          invalidate();
          return;
        }
      }

      // Keep pressure alongside the simplified positions by matching each kept
      // point back to its original sample.
      const kept = simplify(
        positions,
        Math.min(SIMPLIFY_PX / vp.zoom, Math.max(style.size * 0.12, 0.35)),
      );
      const keptSet = new Set(kept.map((p) => `${p.x},${p.y}`));
      const finalPoints = points.filter((p) => keptSet.has(`${p[0]},${p[1]}`));

      const node = createDraw(
        finalPoints.length > 1 ? finalPoints : points,
        {
          userId: next.identity.userId,
          index: doc.topIndex(),
        },
        {
          color: style.color,
          size: style.size,
          brush: style.brush,
        },
      );
      doc.add(node);
      doc.breakUndoBatch();
      invalidate();
    },

    cancel() {
      scratch.drawPoints = null;
      scratch.gestureActive = false;
      doc.setPresence({ draft: null });
      invalidate();
    },
  };
}

const round = (value: number, factor: number) => Math.round(value * factor) / factor;

/**
 * A remnant of a rotated stroke gets its own, smaller box — and a node rotates
 * about its own box centre, not the centre of the stroke it was cut from. Left
 * alone, every piece would swing to a new place the moment the eraser lifted.
 * Shifting the points by the difference between the two pivots (rotated and
 * unrotated) puts the piece back exactly where that ink was on screen.
 */
function keepRotatedPlacement(points: DrawPoint[], pivot: Vec, rotation: number): DrawPoint[] {
  if (rotation === 0) return points;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    if (p[0] < minX) minX = p[0];
    if (p[0] > maxX) maxX = p[0];
    if (p[1] < minY) minY = p[1];
    if (p[1] > maxY) maxY = p[1];
  }
  const own: Vec = { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };
  const spun = vRotate(own, pivot, rotation);
  const dx = spun.x - own.x;
  const dy = spun.y - own.y;
  return points.map((p) => [p[0] + dx, p[1] + dy, p[2]] as DrawPoint);
}

/**
 * Object eraser for shapes/stickies, and a true stroke eraser for freehand ink:
 * sweeping across a line only removes the part under the tip, splitting the
 * stroke when the pass cuts through the middle.
 */
export function createEraseGesture(ctx: GestureContext): Gesture {
  const doc = ctx.doc;
  const erased = new Set<NodeId>();
  /** Draw nodes already rewritten this sweep — avoid double-processing leftovers. */
  const touchedDraw = new Set<NodeId>();

  const sweep = (at: Vec) => {
    const vp = useEditor.getState().viewport;
    const radius = scratch.eraserRadius;
    const area = { x: at.x - radius, y: at.y - radius, w: radius * 2, h: radius * 2 };
    const doomed: NodeId[] = [];
    const replacements: ReturnType<typeof createDraw>[] = [];

    for (const node of doc.all()) {
      // Containers and anchored content are off limits: removing a frame
      // cascades to everything inside it, and a comment pin is a thread.
      if (
        node.locked ||
        node.hidden ||
        erased.has(node.id) ||
        touchedDraw.has(node.id) ||
        node.type === 'group' ||
        node.type === 'image' ||
        node.type === 'notebook' ||
        node.type === 'frame' ||
        node.type === 'comment' ||
        node.type === 'embed'
      ) {
        continue;
      }
      if (!rectIntersects(area, nodeAABB(node, doc.lookup))) continue;

      if (node.type === 'draw') {
        const reach = radius + node.size / 2;
        // Broad reject: eraser nowhere near the polyline.
        if (
          !hitTestNode(node, at, {
            lookup: doc.lookup,
            tolerance: reach,
            frameEdgeOnly: true,
          })
        ) {
          continue;
        }
        const world = drawWorldPoints(node);
        if (world.length === 0) continue;
        const worldPts: DrawPoint[] = world.map((p, i) => [
          p.x,
          p.y,
          node.points[i]?.[2] ?? 0.5,
        ]);

        // Stroke points live in the node's un-rotated box space; match hit-testing.
        const localAt =
          node.rotation === 0
            ? at
            : vRotate(at, rectCenter(nodeBox(node)), -node.rotation);
        const remnants = eraseStrokeAt(worldPts, localAt, reach);
        const unchanged =
          remnants.length === 1 &&
          remnants[0]!.length === worldPts.length &&
          remnants[0]!.every((p, i) => p[0] === worldPts[i]![0] && p[1] === worldPts[i]![1]);
        if (unchanged) continue;

        touchedDraw.add(node.id);
        doomed.push(node.id);
        erased.add(node.id);

        const factoryCtx = { userId: ctx.identity.userId, index: node.index };
        const pivot = rectCenter(nodeBox(node));
        for (const pts of remnants) {
          if (pts.length < 2) continue;
          replacements.push(
            createDraw(keepRotatedPlacement(pts, pivot, node.rotation), factoryCtx, {
              color: node.color,
              size: node.size,
              brush: node.brush,
              closed: false,
              opacity: node.opacity,
              parentId: node.parentId,
              rotation: node.rotation,
            }),
          );
        }
        continue;
      }

      if (
        hitTestNode(node, at, {
          lookup: doc.lookup,
          tolerance: radius,
          frameEdgeOnly: true,
        })
      ) {
        doomed.push(node.id);
        erased.add(node.id);
      }
    }

    if (doomed.length > 0) doc.remove(doomed);
    if (replacements.length > 0) doc.addMany(replacements);
    scratch.eraserPoint = at;
    scratch.eraserRadius = 14 / vp.zoom;
    invalidate();
  };

  scratch.eraserRadius = 14 / useEditor.getState().viewport.zoom;
  scratch.gestureActive = true;
  sweep(ctx.pointer.board);

  return {
    cursor: 'none',
    move(next) {
      sweep(next.pointer.board);
    },
    up() {
      scratch.eraserPoint = null;
      scratch.gestureActive = false;
      doc.breakUndoBatch();
      invalidate();
    },
    cancel() {
      scratch.eraserPoint = null;
      scratch.gestureActive = false;
      invalidate();
    },
  };
}

/** Trail length kept for the laser pointer. */
const LASER_MAX_POINTS = 48;

/**
 * Laser pointer: a fading trail that is never written to the document. It
 * travels over the presence channel, so it costs nothing to undo and vanishes
 * when the presenter stops.
 */
export function createLaserGesture(ctx: GestureContext): Gesture {
  const doc = ctx.doc;
  const trail: Vec[] = [ctx.pointer.board];
  const self = ctx.identity.userId;
  let lastBroadcast = 0;

  const broadcast = (force = false) => {
    const now = performance.now();
    if (!force && now - lastBroadcast < DRAFT_BROADCAST_MS) return;
    lastBroadcast = now;
    doc.setPresence({ laser: trail.slice() });
  };

  const push = (p: Vec) => {
    trail.push(p);
    if (trail.length > LASER_MAX_POINTS) trail.splice(0, trail.length - LASER_MAX_POINTS);
    scratch.lasers.set(self, {
      points: trail.slice(),
      updatedAt: performance.now(),
      color: ctx.identity.color,
    });
    broadcast();
    invalidate();
  };

  push(ctx.pointer.board);

  return {
    cursor: 'crosshair',
    coalesce: true,
    move(next) {
      const vp = useEditor.getState().viewport;
      const last = trail[trail.length - 1]!;
      if (vDist(last, next.pointer.board) < slop(vp, 2)) return;
      push(next.pointer.board);
    },
    up() {
      doc.setPresence({ laser: null });
      invalidate();
    },
    cancel() {
      scratch.lasers.delete(self);
      doc.setPresence({ laser: null });
      invalidate();
    },
  };
}
